import type { LabOrderStatus } from '@synapse/lab-tests';
import type { LabWorklistRow } from '@/types/lab';

// Pure helpers behind the worklist. Split out so the segment/faceting rules can
// be tested without rendering, the way `visitRecency.ts` sits behind the visit
// timeline.

export type WorklistSegment = 'toCollect' | 'inLab' | 'resulted' | 'all';

/**
 * Tab order for the worklist strip. `all` leads because it is also the default
 * segment: a default sitting in position two reads as a filter someone else
 * left on, and the lab desk opens this page to see the day, not one slice of it.
 */
export const WORKLIST_SEGMENTS: WorklistSegment[] = ['all', 'toCollect', 'inLab', 'resulted'];

/**
 * Which derived statuses each segment shows.
 *
 * `toCollect` deliberately covers both "never drawn" and "drawn, rejected, needs
 * a redraw": from the collector's side they are the same job, and separating
 * them would leave a re-collection sitting in a segment nobody works from. The
 * ROW still says which it is, via the status chip.
 */
const SEGMENT_STATUSES: Record<WorklistSegment, LabOrderStatus[] | null> = {
  toCollect: ['awaiting_collection', 'recollect_required'],
  inLab: ['collected', 'partially_resulted'],
  resulted: ['resulted'],
  all: null,
};

export function segmentMatches(segment: WorklistSegment, status: LabOrderStatus): boolean {
  const statuses = SEGMENT_STATUSES[segment];
  return statuses === null ? true : statuses.includes(status);
}

/**
 * Facet the fetched window into segments.
 *
 * Counts are computed over the LOADED rows, not queried separately, and that is
 * a deliberate limit rather than an oversight: an order's status is derived from
 * its tests and specimens, so there is no status column to count in SQL, and
 * faking a count with a second query would mean re-deriving the rule there. The
 * window is a date range (default: today), which for a clinic is tens of rows —
 * so "the segments describe the orders in this range" is both true and useful.
 *
 * When the server's page cap is hit the caller shows `capNotice`, because a
 * silently truncated count reads as a complete one.
 */
export function facetByStatus(
  rows: LabWorklistRow[],
): Record<WorklistSegment, LabWorklistRow[]> {
  return {
    toCollect: rows.filter((r) => segmentMatches('toCollect', r.status)),
    inLab: rows.filter((r) => segmentMatches('inLab', r.status)),
    resulted: rows.filter((r) => segmentMatches('resulted', r.status)),
    all: rows,
  };
}

/** `yyyy-mm-dd` for a Date, in the viewer's own timezone. */
export function toIsoDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** The default worklist window: today only. */
export function defaultRange(now: Date = new Date()): { from: string; to: string } {
  const today = toIsoDate(now);
  return { from: today, to: today };
}

/**
 * A one-line summary of an order's tests for the row.
 *
 * Cancelled tests are dropped — a requisition that asked for four things and
 * cancelled two asks for two.
 */
export function liveTestNames(row: LabWorklistRow): string[] {
  return row.tests.filter((t) => t.status !== 'cancelled').map((t) => t.name);
}

/** Distinct live specimen types on an order, for the pills. */
export function liveSpecimenTypes(row: LabWorklistRow): string[] {
  const types = row.tests
    .filter((t) => t.status !== 'cancelled')
    .map((t) => t.specimenType);
  return [...new Set(types)];
}
